/**
 * A voice call on one session, whatever client holds it (RFC 0307 3b to 3d).
 *
 * The call reads its session off the documents, follows the sessions its agent
 * handed work to, writes every line to the log, and hears the log: a question,
 * a finished turn, a blocked subtask, settled mid-turn text. Nothing here
 * knows a window or a microphone. A client opens one with what only it knows
 * (its screen, how it routes a sent line, how it hangs up) and connects the
 * returned plan to its audio adapter.
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { projectSessionTranscript, sessionTitleOf } from '../../model'
import { cancelRun } from '../run/cancel-run'
import { appendSpokenLine, relaySpokenLine } from '../run/enqueue-run'
import {
  buildVoicePlan,
  liveNotice,
  type SessionVoiceState,
  voiceNotices,
} from './plan'
import { answerSpoken, delegatedSessionIds, sessionVoiceState } from './state'

/** A call nobody has spoken on for this long hangs up (RFC 0307 5, cost). */
const IDLE_HANG_UP_MS = 5 * 60_000
/** The session is re-read at most this often while a call is live. */
const READ_EVERY_MS = 400
/** Mid-turn text reaches the voice once it has stopped changing this long. */
const LIVE_SETTLE_MS = 3000

/** What the session needs of a live call: a way to tell the voice something. */
export interface VoiceNotifier {
  notify(text: string, opts?: { speak?: boolean }): void
}

export interface VoiceCallSessionOptions {
  repo: Repo
  sessionId: string
  projectId?: string
  /** The session's name as the call should speak it. */
  title: () => string
  /** End the call: the idle hang-up calls this. */
  hangUp: () => void
  /** The registry renamed the session (a stub title replaced after a turn). */
  retitle?: (title: string) => void
  /** What the person's screen shows, when the client has one. */
  screen?: () => Promise<string>
  /** How a line sent home is addressed: the model and harness a typed send would use. */
  address?: () => {
    requestedModel?: { provider: string; model: string }
    agent?: string
  }
  /** Hand the call back to the World agent (RFC 0336 3d), where the client
   *  has one; false when the call is already on it. */
  backToWorld?: () => Promise<boolean> | boolean
  /** The project a session is in, where the client knows it (RFC 0336 S1):
   *  an answer to a session in another project is recorded there. */
  projectOf?: (sessionId: string) => string | undefined
}

export function voiceCallSession(opts: VoiceCallSessionOptions) {
  const { repo, hangUp } = opts
  /**
   * The session the call is on. `let`, because a put-through moves the call to
   * another session without hanging up (RFC 0336 S8): every closure below
   * reads these, so re-targeting is changing them and re-reading the log.
   */
  let sessionId = opts.sessionId
  let projectId = opts.projectId
  const retitle = opts.retitle ?? (() => {})
  const voiceCallId = crypto.randomUUID()
  let sessionDoc: unknown
  let projectDoc: unknown
  /** The open documents, once the call is live. Read through, never cached. */
  let sessionHandle: DocHandle<unknown> | null = null
  let projectHandle: DocHandle<unknown> | null = null
  let state: SessionVoiceState = { status: 'idle', subtasks: [] }
  // The sessions this one handed work to, followed while the call is live.
  const followed = new Map<string, DocHandle<unknown> | null>()
  let onDocChange: () => void = () => {}
  let following = false
  const follow = (id: string) => {
    if (followed.has(id) || !following) return
    followed.set(id, null)
    void repo
      .find(id as AnyDocumentId)
      .then((h) => {
        if (!following) return
        const handle = h as DocHandle<unknown>
        followed.set(id, handle)
        handle.on('change', onDocChange)
        onDocChange()
      })
      .catch(() => followed.delete(id))
  }
  /** The session now. Pure of `state`, which only the notices advance. */
  const compute = (): SessionVoiceState => {
    // From the handles, not the copy the last change event left: those events
    // are not reliable here, and a missed one made session_state answer from
    // before the agent's reply.
    if (sessionHandle) sessionDoc = sessionHandle.doc()
    if (projectHandle) projectDoc = projectHandle.doc()
    const transcript = projectSessionTranscript(sessionDoc)
    const own = sessionVoiceState(sessionDoc, projectDoc, sessionId, transcript)
    const others: NonNullable<SessionVoiceState['others']> = []
    for (const id of delegatedSessionIds(transcript)) {
      if (id === sessionId) continue
      follow(id)
      const doc = followed.get(id)?.doc()
      if (!doc) continue
      const { others: _nested, ...theirs } = sessionVoiceState(
        doc,
        projectDoc,
        id,
        projectSessionTranscript(doc),
      )
      others.push({
        sessionId: id,
        title:
          registryTitle(projectDoc, id) ||
          String((doc as { title?: unknown }).title ?? 'another session'),
        ...theirs,
      })
    }
    return others.length ? { ...own, others } : own
  }
  const read = () => {
    state = compute()
    return state
  }
  let lastHeard = Date.now()
  /** The live call, so the plan can ask the voice to speak (3e). */
  let live: VoiceNotifier | null = null
  /** Re-reads the session's documents after a re-target; set while live. */
  let rebind: (() => Promise<void>) | null = null

  const backToWorld = opts.backToWorld
  /**
   * Where each spoken line was written. A put-through can land between a line
   * and its relay (the quiet timer), and the line is relayed from the session
   * it was said to, not the one the call is on by then (RFC 0336 S8).
   */
  const lineHome = new Map<string, { sessionId: string; projectId?: string }>()
  const projectFor = (id: string): string | undefined =>
    opts.projectOf?.(id) ?? (id === sessionId ? projectId : undefined)
  const plan = buildVoicePlan({
    sessionTitle: opts.title,
    ...(backToWorld
      ? { backToWorld: async () => Boolean(await backToWorld()) }
      : {}),
    appendLine: async (line) => {
      const home = {
        sessionId,
        ...(projectId ? { projectId } : {}),
      }
      const id = await appendSpokenLine(repo, home.sessionId, {
        voiceCallId,
        ...line,
      })
      lineHome.set(String(id), home)
      return id
    },
    relay: async (entryId, addressedSessionId) => {
      const home = lineHome.get(String(entryId)) ?? {
        sessionId,
        ...(projectId ? { projectId } : {}),
      }
      const homeDoc =
        home.sessionId === sessionId
          ? sessionDoc
          : (await repo.find(home.sessionId as AnyDocumentId)).doc()
      if (addressedSessionId) {
        if (
          !delegatedSessionIds(projectSessionTranscript(homeDoc)).includes(
            addressedSessionId,
          )
        )
          throw new Error('session is not followed by this call')
        await relaySpokenLine(repo, home.sessionId, entryId as never, {
          ...(home.projectId ? { projectId: home.projectId } : {}),
          to: addressedSessionId,
        })
        return
      }
      // The same address a typed send gets, so a multi-harness client routes a
      // relayed line to the same agent. The client knows it; core does not.
      const address = opts.address?.() ?? {}
      await relaySpokenLine(repo, home.sessionId, entryId as never, {
        ...(home.projectId ? { projectId: home.projectId } : {}),
        ...(address.requestedModel
          ? { requestedModel: address.requestedModel }
          : {}),
        ...(address.agent ? { agent: address.agent } : {}),
      })
    },
    // session_state reads the session as it is now (3b). It does not move
    // `state`: that is what the next notice compares against, and moving it
    // here would swallow a turn the voice was never told had ended.
    state: () => (sessionHandle ? compute() : state),
    ...(opts.screen ? { screen: opts.screen } : {}),
    answer: async (text, addressedSessionId, parts) => {
      const targetId = addressedSessionId ?? sessionId
      // The call follows only work its home agent handed off. A voice tool
      // cannot use an arbitrary id to answer an unrelated session's question.
      if (
        targetId !== sessionId &&
        !delegatedSessionIds(projectSessionTranscript(sessionDoc)).includes(
          targetId,
        )
      )
        return false
      const targetDoc =
        targetId === sessionId
          ? sessionDoc
          : (await repo.find(targetId as AnyDocumentId)).doc()
      // Recorded in the answered session's own project: its task, its row.
      const targetProject = projectFor(targetId)
      return answerSpoken(
        {
          repo,
          sessionId: targetId,
          ...(targetProject ? { projectId: targetProject } : {}),
          sessionDoc: targetDoc,
        },
        text,
        parts,
      )
    },
    say: (instruction) => live?.notify(instruction, { speak: true }),
    stopAgent: async (addressedSessionId) => {
      // The work may run in a session this one handed it to; only those, and
      // home, can be stopped from this call.
      if (
        addressedSessionId &&
        addressedSessionId !== sessionId &&
        !delegatedSessionIds(projectSessionTranscript(sessionDoc)).includes(
          addressedSessionId,
        )
      )
        throw new Error('session is not followed by this call')
      await cancelRun(repo, addressedSessionId ?? sessionId)
    },
  })

  return {
    ...plan,
    /**
     * Move the call to another session without hanging up (RFC 0336 S8).
     *
     * The World agent put the person through, so from here what they say goes
     * to that session. The sessions the old one handed work to are no longer
     * followed; the new one's log is read, and the voice is told, out loud,
     * who it is talking to now.
     */
    retarget: (
      next: { sessionId: string; projectId?: string },
      say: string,
    ) => {
      if (next.sessionId === sessionId) return
      sessionId = next.sessionId
      projectId = next.projectId
      for (const h of followed.values()) h?.off('change', onDocChange)
      followed.clear()
      state = { status: 'idle', subtasks: [] }
      sessionHandle = null
      projectHandle = null
      sessionDoc = undefined
      projectDoc = undefined
      void rebind?.()
      live?.notify(say, { speak: true })
    },
    onTranscript: (t: { speaker: 'person' | 'voice'; text: string }) => {
      if (t.speaker === 'person') lastHeard = Date.now()
      plan.onTranscript(t)
    },
    onLive: (call: VoiceNotifier) => {
      live = call
      let stopped = false
      let timer: ReturnType<typeof setTimeout> | null = null
      let settle: ReturnType<typeof setTimeout> | null = null
      let saidLive = ''
      const handles: DocHandle<unknown>[] = []
      following = true
      // Mid-turn text streams in; the voice hears it once it stops changing.
      // The live text that matters: this session's, or else the busy session
      // the work was handed to.
      const liveNow = (): { text: string; where?: string } => {
        if (state.status === 'running' && state.live?.kind === 'text')
          return { text: state.live.text.trim() }
        const other = state.others?.find(
          (o) => o.status === 'running' && o.live?.kind === 'text',
        )
        return other?.live
          ? { text: other.live.text.trim(), where: other.title }
          : { text: '' }
      }
      const watchLive = () => {
        const { text, where } = liveNow()
        if (settle) clearTimeout(settle)
        settle = null
        if (!text || text === saidLive) return
        settle = setTimeout(() => {
          settle = null
          if (stopped || liveNow().text !== text) return
          saidLive = text
          // Silent: the voice knows it, and says it only when asked.
          call.notify(
            where ? `In "${where}": ${liveNotice(text)}` : liveNotice(text),
          )
        }, LIVE_SETTLE_MS)
      }
      // The voice hears the log (3d): each change, compared with the last.
      const changed = () => {
        if (timer || stopped) return
        timer = setTimeout(() => {
          timer = null
          if (stopped) return
          sessionDoc = handles[0]?.doc()
          if (handles[1]) projectDoc = handles[1].doc()
          retitle(registryTitle(projectDoc, sessionId))
          const prev = state
          // A question, a finished turn, a blocked subtask: these need the
          // person. All of one change go as ONE spoken notice, so a turn that
          // ends here and in a session it handed work to is one reply.
          const said = voiceNotices(prev, read())
          if (said.length) call.notify(said.join('\n\n'), { speak: true })
          watchLive()
        }, READ_EVERY_MS)
      }
      // A followed session's change is read the same way as this one's.
      onDocChange = changed
      // Each bind is numbered, so a slow lookup from a re-target that has
      // since been superseded cannot install the old session's handles.
      let binding = 0
      const bind = async () => {
        const mine = ++binding
        for (const h of handles) h.off('change', changed)
        handles.length = 0
        const session = await repo.find(sessionId as AnyDocumentId)
        const project = projectId
          ? await repo.find(projectId as AnyDocumentId)
          : null
        if (stopped || mine !== binding) return
        handles.push(session as DocHandle<unknown>)
        if (project) handles.push(project as DocHandle<unknown>)
        sessionHandle = session as DocHandle<unknown>
        projectHandle = (project as DocHandle<unknown> | null) ?? null
        sessionDoc = session.doc()
        projectDoc = project?.doc()
        retitle(registryTitle(projectDoc, sessionId))
        // A question already waiting when the call starts is news: its agent
        // is held up on it. Compared with an empty state, so only questions
        // are said, never turns that finished before the call.
        const said = voiceNotices(
          { status: 'idle', subtasks: [] },
          { ...read(), subtasks: [] },
        )
        if (said.length) call.notify(said.join('\n\n'), { speak: true })
        for (const h of handles) h.on('change', changed)
      }
      rebind = bind
      void bind()
      lastHeard = Date.now()
      const idle = setInterval(() => {
        if (Date.now() - lastHeard > IDLE_HANG_UP_MS) hangUp()
      }, 30_000)
      return () => {
        live = null
        rebind = null
        // Nothing said on this call goes anywhere once it has hung up.
        plan.close?.()
        stopped = true
        following = false
        for (const h of followed.values()) h?.off('change', onDocChange)
        followed.clear()
        if (timer) clearTimeout(timer)
        if (settle) clearTimeout(settle)
        clearInterval(idle)
        for (const h of handles) h.off('change', changed)
      }
    },
  }
}

/** The session's title in its project's registry, or '' when unknown. */
function registryTitle(projectDoc: unknown, sessionId: string): string {
  const meta = (
    projectDoc as { sessionMeta?: Record<string, unknown> } | undefined
  )?.sessionMeta?.[sessionId]
  return meta ? String(sessionTitleOf(meta as never) ?? '') : ''
}
